import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchTagsThunk, loadMoreTagsThunk, addTagThunk, deleteTagThunk, editTagThunk, updateTagThunk } from '@/state/tags/thunks';
import { PAGE_LENGTH } from '@/utils/constants/constants';
import { buildListCacheKey, invalidateListCacheByPage, writeListCache } from '@/utils/listPageCache';

export interface Tag {
  id: string;
  name: string;
  [key: string]: any;
}

interface TagsState {
  tags: Tag[];
  tagHistory: Tag[];
  isLoading: boolean;
  loadData: boolean;
  totalCount: number;
  tagLeft: boolean;
  allFetched: boolean;
  pageNo: number;
  dataLength: number;
  sorting: any;
  filter: any;
  searchItem: string | null;
  error: string | null;
}

const initialState: TagsState = {
  tags: [],
  tagHistory: [],
  isLoading: false,
  loadData: false,
  totalCount: 0,
  tagLeft: true,
  allFetched: false,
  pageNo: 1,
  dataLength: PAGE_LENGTH,
  sorting: { created_at: -1 },
  filter: {},
  searchItem: null,
  error: null,
};

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    setPageNo: (state, action: PayloadAction<number>) => {
      state.pageNo = action.payload;
    },
    setDataLength: (state, action: PayloadAction<number>) => {
      state.dataLength = action.payload;
    },
    setSorting: (state, action: PayloadAction<any>) => {
      state.sorting = action.payload;
    },
    setFilter: (state, action: PayloadAction<any>) => {
      state.filter = action.payload;
    },
    setSearchItem: (state, action: PayloadAction<string | null>) => {
      state.searchItem = action.payload;
      state.pageNo = 1; // Reset to page 1 when search changes
    },
    setTags: (state, action: PayloadAction<Tag[]>) => {
      state.tags = action.payload;
      state.tagHistory = action.payload;
    },
    updateTagInList: (state, action: PayloadAction<Tag>) => {
      const updatedTag = action.payload;
      const tagIndex = state.tags.findIndex((tag) => tag.id === updatedTag.id);
      if (tagIndex !== -1) {
        state.tags[tagIndex] = { ...state.tags[tagIndex], ...updatedTag };
        state.tagHistory = [...state.tags];
      }
    },
    resetTagsState: (state) => {
      state.pageNo = 1;
      state.dataLength = PAGE_LENGTH;
      state.sorting = { created_at: -1 };
      state.filter = {};
      state.searchItem = null;
      state.tagLeft = true;
      state.allFetched = false;
    },
    resetResponse: (state) => {
      state.tagLeft = true;
      state.allFetched = false;
    },
    hydrateFromCache: (state, action: PayloadAction<{ items: Tag[]; totalCount: number }>) => {
      state.tags = action.payload.items;
      state.tagHistory = action.payload.items;
      state.totalCount = action.payload.totalCount;
      state.isLoading = false;
      state.loadData = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tags
      .addCase(fetchTagsThunk.pending, (state) => {
        if (state.pageNo === 1) {
          state.isLoading = true;
          state.loadData = true;
        } else {
          state.isLoading = true;
        }
        state.tagLeft = true;
        state.allFetched = false;
        state.error = null;
      })
      .addCase(fetchTagsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loadData = false;
        state.totalCount = action.payload.total_count;
        state.tags = action.payload.tags;
        state.tagHistory = action.payload.tags;

        if (state.pageNo === 1) {
          const cacheKey = buildListCacheKey('tags', {
            dataLength: state.dataLength,
            sorting: state.sorting,
            searchItem: state.searchItem,
            filter: state.filter,
          });
          writeListCache(cacheKey, action.payload.tags, action.payload.total_count);
        }
        
        if (action.payload.tags.length < state.dataLength) {
          state.tagLeft = false;
          state.allFetched = true;
        }
        state.error = null;
      })
      .addCase(fetchTagsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.loadData = false;
        state.error = action.error.message || 'Failed to fetch tags';
      })
      // Load more tags
      .addCase(loadMoreTagsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadMoreTagsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.totalCount = action.payload.total_count;
        state.pageNo = state.pageNo + 1;
        state.tags = [...state.tags, ...action.payload.tags];
        state.tagHistory = [...state.tags, ...action.payload.tags];
        
        if (action.payload.tags.length < state.dataLength) {
          state.tagLeft = false;
          state.allFetched = true;
        }
        state.error = null;
      })
      .addCase(loadMoreTagsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load more tags';
      })
      // Add tag
      .addCase(addTagThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(addTagThunk.fulfilled, (state, action) => {
        state.error = null;
        invalidateListCacheByPage('tags');
        // Add the new tag to the beginning of the list
        if (action.payload) {
          const newTag = action.payload;
          state.tags = [newTag, ...state.tags];
          state.tagHistory = [...state.tags];
          state.totalCount = state.totalCount + 1;
        }
      })
      .addCase(addTagThunk.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add tag';
      })
      // Delete tag
      .addCase(deleteTagThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteTagThunk.fulfilled, (state, action) => {
        state.error = null;
        invalidateListCacheByPage('tags');
        // Remove the deleted tag from the list
        if (action.payload) {
          const deletedTagId = action.payload;
          state.tags = state.tags.filter((tag) => 
            String(tag.id) !== String(deletedTagId) && tag.id !== deletedTagId
          );
          state.tagHistory = [...state.tags];
          state.totalCount = Math.max(0, state.totalCount - 1);
        }
      })
      .addCase(deleteTagThunk.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete tag';
      })
      // Edit tag
      .addCase(editTagThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(editTagThunk.fulfilled, (state, action) => {
        state.error = null;
        invalidateListCacheByPage('tags');
        // Update only the edited tag in the list
        if (action.payload) {
          const { tag: updatedTag, tagId } = action.payload;
          // Try to find tag by id (handle both string and number)
          const tagIndex = state.tags.findIndex((tag) => 
            String(tag.id) === String(tagId) || tag.id === tagId
          );
          if (tagIndex !== -1) {
            // Merge the updated data with existing tag data
            state.tags[tagIndex] = { ...state.tags[tagIndex], ...updatedTag };
            state.tagHistory = [...state.tags];
          }
        }
      })
      .addCase(editTagThunk.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to edit tag';
      })
      // Update tag (for color updates, etc.)
      .addCase(updateTagThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTagThunk.fulfilled, (state, action) => {
        state.error = null;
        invalidateListCacheByPage('tags');
        // Update only the updated tag in the list
        if (action.payload) {
          const { tag: updatedTag, tagId } = action.payload;
          // Try to find tag by id (handle both string and number)
          const tagIndex = state.tags.findIndex((tag) => 
            String(tag.id) === String(tagId) || tag.id === tagId
          );
          if (tagIndex !== -1) {
            // Merge the updated data with existing tag data
            state.tags[tagIndex] = { ...state.tags[tagIndex], ...updatedTag };
            state.tagHistory = [...state.tags];
          }
        }
      })
      .addCase(updateTagThunk.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update tag';
      });
  },
});

export const {
  setPageNo,
  setDataLength,
  setSorting,
  setFilter,
  setSearchItem,
  setTags,
  updateTagInList,
  resetTagsState,
  resetResponse,
  hydrateFromCache,
} = tagsSlice.actions;

export default tagsSlice.reducer;

